import React, { useState } from 'react';
import { Plus, Edit, Trash2, Building2 } from 'lucide-react';
import { useCrud } from '../../hooks/useApi';
import { institutionService } from '../../services/api';
import EmptyState from '../../components/Common/EmptyState';
import FormModalLayout from '../../components/Common/FormModalLayout';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import { useTranslation } from 'react-i18next';

const Orgaos = () => {
  const { t } = useTranslation('institution');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({ search: '' });

  const { useList, useCreate, useUpdate, useDelete } = useCrud('orgaos', institutionService.orgaoCrud);
  const { data, isLoading, error } = useList();
  const list = Array.isArray(data) ? data : data?.data ?? [];
  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();

  const filtered = list.filter((o) => !filters.search || (o.name && o.name.toLowerCase().includes(filters.search.toLowerCase())));

  const handleSubmit = (formData) => {
    if (editing) updateMutation.mutate({ id: editing.id, data: formData });
    else createMutation.mutate(formData);
    setShowModal(false);
    setEditing(null);
  };

  if (error)
    return (
      <FormPageShell>
        <div className="alert alert-danger rounded-xl border border-red-200">
          <p className="text-red-800">{t('common:loadError')}</p>
        </div>
      </FormPageShell>
    );

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("orgaosTitle")}
          description="Órgãos da instituição para tramitação e estrutura organizacional."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Instituição', to: '/institution/agencies' },
            { label: 'Órgãos' },
          ]}
        >
          <Can permission={P.INSTITUTION_ACCESS}>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setShowModal(true);
              }}
              className="btn btn-primary inline-flex items-center gap-2 shadow-sm"
            >
              <Plus className="h-4 w-4" /> Novo órgão
            </button>
          </Can>
        </PageHeader>
      <div className="data-list-card p-5 sm:p-6">
        <div className="mb-4">
          <input type="text" placeholder={t('common:searchPlaceholder')} value={filters.search || ''} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="form-input max-w-xs" />
        </div>
        {isLoading ? <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div> : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receber tramitação</th><th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th></tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtered.map((o) => (
                  <tr key={o.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{o.name}</td>
                    <td className="px-6 py-4 text-sm">{o.receive_routing ? 'Sim' : 'Não'}</td>
                    <td className="px-6 py-4 text-right">
                      <Can permission={P.INSTITUTION_ACCESS}>
                        <button type="button" onClick={() => { setEditing(o); setShowModal(true); }} className="text-primary-600 hover:text-primary-900 p-1 mr-2"><Edit className="w-4 h-4 inline" /></button>
                        <button type="button" onClick={() => window.confirm('Excluir?') && deleteMutation.mutate(o.id)} className="text-red-600 hover:text-red-900 p-1"><Trash2 className="w-4 h-4 inline" /></button>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <EmptyState
                icon={Building2}
                title={t("orgaosEmpty")}
                description="Cadastre órgãos para tramitação e estrutura institucional."
                action={
                  <Can permission={P.INSTITUTION_ACCESS}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setEditing(null);
                        setShowModal(true);
                      }}
                    >
                      Novo órgão
                    </button>
                  </Can>
                }
              />
            )}
          </div>
        )}
      </div>
      </div>
      {showModal && (
        <FormModalLayout
          title={editing ? 'Editar órgão' : 'Novo órgão'}
          subtitle="Órgãos participam da estrutura e da tramitação de processos."
          headerIcon={Building2}
          historyEntityType="agencies"
          historyEntityId={editing?.id}
          onClose={() => {
            setShowModal(false);
            setEditing(null);
          }}
          size="md"
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditing(null);
                }}
                className="btn btn-outline"
              >
                Cancelar
              </button>
              <Can permission={P.INSTITUTION_ACCESS}>
                <button type="submit" form="inst-orgao-form" className="btn btn-primary">
                  Salvar
                </button>
              </Can>
            </>
          }
        >
          <form
            id="inst-orgao-form"
            key={editing?.id ?? 'new'}
            onSubmit={(e) => {
              e.preventDefault();
              const el = e.currentTarget;
              handleSubmit({
                name: el.orgaoName?.value || '',
                receive_routing: el.receiveRouting?.checked || false,
              });
            }}
            className="form-stack"
          >
            <div className="form-group">
              <label htmlFor="inst-orgao-name" className="form-label form-label-required">
                Nome do órgão
              </label>
              <input
                id="inst-orgao-name"
                name="orgaoName"
                defaultValue={editing?.name}
                placeholder="Nome do órgão"
                className="form-input"
                required
              />
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input name="receiveRouting" type="checkbox" defaultChecked={editing?.receive_routing} className="form-check" />
              Receber tramitação
            </label>
          </form>
        </FormModalLayout>
      )}
    </FormPageShell>
  );
};

export default Orgaos;
